
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, CalendarDays } from 'lucide-react';
import { Season } from '../types';

interface SeasonSelectorProps {
  seasons: Season[];
  viewingSeason: Season | null; // null = current (active) season
  onChange: (season: Season | null) => void;
}

const SeasonSelector: React.FC<SeasonSelectorProps> = ({ seasons, viewingSeason, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const activeSeason = seasons.find(s => s.isActive);
  const pastSeasons = seasons.filter(s => !s.isActive);

  const displayName = viewingSeason
    ? viewingSeason.name
    : activeSeason
    ? `${activeSeason.name} (Current)`
    : 'Current Season';

  const isHistorical = viewingSeason !== null && !viewingSeason.isActive;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-semibold transition-colors ${
          isHistorical
            ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
        }`}
      >
        <CalendarDays className="w-3.5 h-3.5 shrink-0" />
        <span>{displayName}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden min-w-[200px]">
          {/* Current season option */}
          <button
            type="button"
            onClick={() => { onChange(null); setOpen(false); }}
            className={`w-full text-left px-4 py-2.5 text-sm font-semibold hover:bg-slate-50 transition-colors flex items-center justify-between gap-2 ${!viewingSeason || viewingSeason.isActive ? 'text-blue-700 bg-blue-50' : 'text-slate-700'}`}
          >
            <span>{activeSeason ? `${activeSeason.name}` : 'Current Season'}</span>
            <span className="text-[10px] font-bold bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded uppercase tracking-wide shrink-0">Current</span>
          </button>

          {pastSeasons.length > 0 && (
            <>
              <div className="h-px bg-slate-100" />
              <div className="py-1">
                {pastSeasons.map(season => (
                  <button
                    key={season.id}
                    type="button"
                    onClick={() => { onChange(season); setOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 transition-colors flex items-center justify-between gap-2 ${viewingSeason?.id === season.id ? 'text-amber-700 bg-amber-50 font-semibold' : 'text-slate-600 font-medium'}`}
                  >
                    <span>{season.name}</span>
                    {season.endDate && (
                      <span className="text-[10px] text-slate-400 shrink-0">
                        {season.endDate.slice(0, 4)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}

          {seasons.length === 0 && (
            <div className="px-4 py-3 text-xs text-slate-400 italic">No seasons created yet</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SeasonSelector;
